import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, CardHeader, 
  FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, Radio, RadioGroup, Drawer, 
  Typography, useMediaQuery } from "@mui/material";
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import { useFormik } from "formik";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import { useRecordContext } from "ra-core";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import ReadonlyField from "~/components/input/ReadonlyField";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import { DataAccessPolicyResponseDto, UpsertDataAccessPolicyInput } from "~/gen/api/encrypt";
import { RecPatch } from "~/dataProvider/tools";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { KEY_MANAGEMENT } from "~/constants";


const validationSchema =
  Yup.object().shape({
    description: Yup.string().max(512),
    validationKey: Yup.string(),
  })

  
const EditKey: FC<{
  open: boolean;
  setOpen: (x: boolean) => void;
  disabled?: boolean;
  databaseId?: number;
}> = ({ open, setOpen, disabled, databaseId }) => {
  const hasWritePermit = useHasPermit([KEY_MANAGEMENT])
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const record = useRecordContext<DataAccessPolicyResponseDto&RecPatch>()
  const mode = record !== null && record !== undefined ? 'edit' : 'create'
  const save = useSimpleSaveAction()

  const [fromIdp, setFromIdp] = useState(false)

  const initialValues = {
    submit: '',
    oidcDomain: record?.oidcDomain || '',
    description: record?.description || '',
    validationKey: record?.validationKey || '',
  }

  const recordRef = useRef<boolean | null>(!!record)

  const validate = (values: typeof initialValues) => {
    const errors = {} as any
    const validateIdpAddress = () => {
      const rgx = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$/
      if (!values.oidcDomain) {
        errors['oidcDomain'] = 'OIDC Authority Address is required'
      } else{ 
        try {
          const url = new URL(values.oidcDomain)
          if (url.protocol !== 'https:') {
            errors['oidcDomain'] = 'Should be a https url'
          } else if (!rgx.test(url.hostname)) {
            errors['oidcDomain'] = 'Should be a valid url'
          }
        } catch {
          errors['oidcDomain'] = 'Should be a valid url'
        }
      }
    }
    if (mode === 'create') {
      if (fromIdp) {
        validateIdpAddress()
      }
      if (!fromIdp && !values.validationKey) {
        errors['validationKey'] = 'Public Key Data is required'
      }
    } else if (mode === 'edit') {
      if (record.oidcDomain) {
        validateIdpAddress()
      }
    }
    if (Object.keys(errors).length > 0) { return errors }
  }
  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    if (disabled || !hasWritePermit) { return }
    const options = {
      onSuccess: () => {
        setOpen(false)
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: typeof(err) === 'string' ? err : err.message })
        setSubmitting(false)
      }
    }
    const payload: UpsertDataAccessPolicyInput = {
      ...(mode === 'edit' && record ? record : {}),
      ...(mode === 'create' && databaseId && { databaseId }),
      ...values,
    }
    save(payload, options)
  }, [save, record])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, validate })
  const { errors, isSubmitting, resetForm, setValues } = formikCtrl
  
  const handleClose = useCallback(() => { 
    setOpen(false) 
    recordRef.current = null
    resetForm({
      values: { description: '', oidcDomain: '', validationKey: '', submit: '', }
    })
  }, [setOpen, resetForm])

  useEffect(() => {
    if (!recordRef.current && !!record) {
      recordRef.current = true
      setValues({
        oidcDomain: record.oidcDomain || '',
        description: record.description || '',
        validationKey: record.validationKey || '',
      } as any)
    }
  }, [record, setValues])

  const format = useUserTimeFormat()
  
  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >

      <OdForm formikCtrl={formikCtrl}>

        <Card>
          <CardHeader title={mode === 'create' ? "New Token Validation Key" : "Token Validation Key Info"} />
          <CardContent>
            <Grid
              container
              spacing={4}
            >
              {mode === 'edit' &&
                <Grid item xs={12} >
                  <ReadonlyField disabled={true} label="Create Time" value={format(record?.createdTime || 0, 'PPp')} />
                </Grid>
              }
              {mode === 'create' &&
                <Grid item xs={12} >
                  <FormControl>
                    <FormLabel id="label-choose-key-source">Key</FormLabel>
                    <RadioGroup
                      aria-labelledby="label-choose-key-source"
                      name="choose-key-source"
                      value={fromIdp}
                      onChange={(e)=> setFromIdp('true' === e.target.value)}
                    >
                      <FormControlLabel value={true}  control={<Radio />} label="Import from OIDC Identity Provider" />
                      <FormControlLabel value={false} control={<Radio />} label="Use Local Public Key File" />
                    </RadioGroup>
                  </FormControl>
                </Grid>}
              {((mode === 'create' && fromIdp) || record?.oidcDomain) &&
                <Grid item xs={12} >
                  <TextField 
                    label="OIDC Authority Address*"
                    name="oidcDomain"
                    {...((disabled || !hasWritePermit) && {
                      InputProps: {
                        readOnly: true,
                      }
                    })}
                  />
                </Grid>}
              <Grid item xs={12} >
                <TextField 
                  label="Description" 
                  name="description" 
                  multiline
                  {...((disabled || !hasWritePermit) && {
                    InputProps: {
                      readOnly: true,
                    }
                  })}
                />
              </Grid>
              <Grid item xs={12} >
                {mode === 'create' && hasWritePermit 
                ? (!fromIdp 
                  ? <TextField 
                      label="Public Key Data*" 
                      name="validationKey" 
                      multiline 
                      minRows={5} 
                      helperText="Copy Paste PEM format public key in here"
                    />
                  : null )
                :
                  <PublicKeyDataViewer validationKey={record?.validationKey} />
                }
              </Grid>
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              disabled={isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            {!disabled && hasWritePermit &&
              <Button
                color="primary"
                disabled={isSubmitting}
                type="submit"
                variant="contained"
              >
                Save
              </Button>
            }
          </Box>
        </Card>

      </OdForm>
    </Drawer>
  )
}

export default EditKey;

const PublicKeyDataViewer : FC<{validationKey:string|undefined|null}> = ({
  validationKey,
}) => {
  const keys = useMemo(() => {
    if (!validationKey) { return [] } 
    else {
      const ks = validationKey.split(';')
      const M = 76
      const lines = ks.map(k =>
        k.split('\n').flatMap(l => {
          if (l.length <= M) { return [l] }
          const n = Math.ceil(l.length / M)
          return [...new Array(n).keys()].map(i => l.substring(M * i, M * (i+1)))
        }))
      return lines
    }
  }, [validationKey])
  const [expanded, setExpanded] = useState(0)

  
  if (!validationKey) { return null }
  return (
    <Box>
      <Typography variant="caption">Public Key Data</Typography>
      <Box
        sx={{
          mt: 1,
          border: theme => `1px solid ${theme.palette.divider}`,
          '& div.MuiPaper-root': { borderRadius: 0 },
          '& div.MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(90deg)',
          },
          '& div.MuiAccordionSummary-content': { ml: 1, },
          '& div.MuiAccordionDetails-root': { p: 0, mt: -1, },
          '& pre': {
            p: 1,
            fontSize: '0.75em',
            bgcolor: 'divider',
            overflowX: 'auto',
            '& span.linenumber': {
              display: 'inline-block',
              minWidth: '1.25em',
              paddingRight: '1em',
              textAlign: 'right',
              userSelect: 'none',
              color: 'rgb(98, 114, 164)',
            },
          }
        }}
      >
        {keys.map((key, i) => {
          return (
            <Accordion key={i} expanded={expanded === i} disableGutters elevation={0} square 
              onChange={() => setExpanded(v => v !== i ? i : -1)}
            >
              <AccordionSummary aria-controls={`panel${i}-content"`} id={`panel${i}-header`}
                expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
              >
                <Typography>Key #{i + 1}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <pre>
                    <code>
                      {key.map((line, j) => 
                        <Fragment key={j}>
                          <span>{line}</span><span>{'\r\n'}</span>
                        </Fragment>
                        )}
                    </code>
                  </pre>
                </Box>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    </Box>
  )
}
